import { useContext, useEffect, useMemo, useState } from "react";
import { Row, Col, Spin, Empty, Tag, Typography } from "antd";
import { ThunderboltOutlined, LinkOutlined } from "@ant-design/icons";
import { useClient } from "hooks/useClient";
import DistributionPointSelected from "context/MyDistributionPoint";
import input_equipment from "assets/images/input_equipment.svg";
import {
  getAllEquipmentsByApplicationType,
  ResponseInputEquipments,
} from "api/generated";
import RegEntradaCard from "./components/RegEntradaCard";
import { handleAxiosError } from "common/helpers/generalUtils";

function RegEntradaScreen() {
  const { client } = useClient();
  const { distributionPoint } = useContext(DistributionPointSelected);
  const [equipments, setEquipments] = useState<ResponseInputEquipments[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const counters = useMemo(() => {
    const total = equipments.length;
    const linked = equipments.filter(
      (e) => e.serialNumber !== null && e.serialNumber !== undefined,
    ).length;
    const active = equipments.filter((e) => e.isActive).length;
    const pending = equipments.filter(
      (e) => e.status === "PENDIENTE_ENLACE",
    ).length;
    return { total, linked, active, pending };
  }, [equipments]);

  const fetchEquipments = () => {
    if (!distributionPoint?.id) return;

    setIsLoading(true);
    getAllEquipmentsByApplicationType({
      client,
      path: { distributionPointId: distributionPoint.id },
      query: { applicationType: "REGENTRADA" },
    })
      .then(({ data }) => {
        setEquipments(data ?? []);
      })
      .catch((error) =>
        handleAxiosError(error, "Error al obtener equipos de entrada:"),
      )
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    fetchEquipments();
  }, [distributionPoint, client]);

  if (!distributionPoint) {
    return <Empty description="Selecciona un punto de distribución" />;
  }

  return (
    <Spin spinning={isLoading} size="large">
      <div className="page-wrapper">
        <div className="page-header">
          <div className="page-header__left">
            <Typography.Title level={4} className="page-header__title">
              Equipos de Entrada
            </Typography.Title>
            <Typography.Text className="page-header__subtitle">
              {distributionPoint.name}
            </Typography.Text>

            {equipments.length > 0 && (
              <div className="page-header__counters">
                <Tag style={{ borderRadius: 20, padding: "4px 12px" }}>
                  <ThunderboltOutlined />
                  <strong> {counters.total} </strong>
                  total
                </Tag>

                <Tag
                  color="success"
                  style={{ borderRadius: 20, padding: "4px 12px" }}
                >
                  <LinkOutlined />
                  <strong> {counters.linked} </strong>
                  enlazados
                </Tag>

                {counters.pending > 0 && (
                  <Tag
                    color="warning"
                    style={{ borderRadius: 20, padding: "4px 12px" }}
                  >
                    <LinkOutlined />
                    <strong> {counters.pending} </strong>
                    pendientes
                  </Tag>
                )}
              </div>
            )}
          </div>
        </div>

        {equipments.length === 0 ? (
          <div className="page-empty">
            <img
              src={input_equipment}
              alt="Equipo de entrada"
              style={{ width: 80, opacity: 0.6, marginBottom: 12 }}
            />
            <Typography.Text className="page-empty__text">
              Sin equipos de entrada agregados
            </Typography.Text>
          </div>
        ) : (
          <Row gutter={[16, 16]}>
            {equipments.map((equipment) => (
              <Col key={equipment.idEquipment} xs={24} sm={12} lg={8} xl={6}>
                <RegEntradaCard equipment={equipment} />
              </Col>
            ))}
          </Row>
        )}
      </div>
    </Spin>
  );
}

export default RegEntradaScreen;
